import React from 'react';
import { FilaPrestacion, FilaCalculada, ModoFila } from '../../types/Calculadora';
import { formatCurrency2, formatPct } from './calculadoraHelpers';
import NumberInput from './NumberInput';
import { Card, Button, Segmented, CalcChip, AnimatedBadge } from '../ui';

interface Props {
  filas: FilaPrestacion[];
  calculadas: FilaCalculada[];
  planNombre: string;
  onChangeFila: (index: number, patch: Partial<FilaPrestacion>) => void;
  onRemoveFila: (index: number) => void;
  onAddFila: () => void;
  onAplicarATodos: () => void;
}

const MODO_OPCIONES: { value: ModoFila; label: string; title: string }[] = [
  {
    value: 'gappex',
    label: 'Gappex',
    title: 'Fijás el Precio Gappex y se calcula lo que pone Digital',
  },
  {
    value: 'prestador',
    label: 'Digital',
    title: 'Fijás lo que pone Digital y se calcula el Precio Gappex',
  },
];

const PrestacionesTable: React.FC<Props> = ({
  filas,
  calculadas,
  planNombre,
  onChangeFila,
  onRemoveFila,
  onAddFila,
  onAplicarATodos,
}) => (
  <Card className="p-6">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
      <div>
        <h2 className="text-base font-semibold text-gappex-secondary">
          Estructura de prestaciones y costos unitarios
        </h2>
        <p className="text-xs text-gray-400 mt-1">
          Cada fila se define de una de dos formas: fijás el{' '}
          <strong className="text-gray-500">Precio Gappex</strong> y se calcula lo que pone Digital, o
          fijás el <strong className="text-gray-500">Digital a prestador</strong> y se calcula el
          precio que habría que acordar.
        </p>
      </div>
      <Button
        variant="secondary"
        onClick={onAplicarATodos}
        title="Copia precios y cantidades de esta tabla al resto de los planes. Cada plan conserva su % de descuento."
        className="shrink-0"
      >
        Aplicar precios a todos los planes
      </Button>
    </div>

    <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-500">
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-sky-50 border border-sky-500" />
        Editable
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-slate-100" />
        Calculado
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-400" />
        El descuento no le cuesta nada a Gappex
      </span>
    </div>

    <div className="overflow-x-auto -mx-6 px-6">
      <table className="w-full text-sm min-w-[1240px]">
        <thead>
          <tr className="text-left text-xs text-gray-500 uppercase border-b">
            <th className="pb-2 pr-2 w-8" title="Apagá la fila para sacarla del cálculo">
              <span className="sr-only">Activa</span>
            </th>
            <th className="pb-2 pr-3">Prestación</th>
            <th className="pb-2 pr-3">Modo</th>
            <th className="pb-2 pr-3 text-right">% Desc.</th>
            <th className="pb-2 pr-3 text-right">Particular</th>
            <th className="pb-2 pr-3 text-right" title="Precio de referencia. No entra en el cálculo.">
              OSDE
            </th>
            <th className="pb-2 pr-3 text-right">Precio Gappex</th>
            <th className="pb-2 pr-3 text-right">Pago paciente</th>
            <th className="pb-2 pr-3 text-right">Digital a prestador</th>
            <th className="pb-2 pr-3 text-right">Cant. mín</th>
            <th className="pb-2 pr-3 text-right">Cant. máx</th>
            <th className="pb-2 text-right">Costo anual / usuario</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((fila, index) => {
            const calc = calculadas[index];
            const apagada = !fila.activa;
            const modoGappex = fila.modo === 'gappex';

            return (
              <tr
                key={fila.id}
                className={`border-b last:border-0 transition-colors duration-100 ${
                  apagada
                    ? 'opacity-45'
                    : calc.sinCostoParaGappex
                      ? 'bg-emerald-50/60 hover:bg-emerald-50'
                      : 'hover:bg-slate-50/70'
                }`}
              >
                <td className="py-2 pr-2">
                  <input
                    type="checkbox"
                    checked={fila.activa}
                    onChange={(e) => onChangeFila(index, { activa: e.target.checked })}
                    aria-label={`Incluir ${fila.nombre} en el cálculo`}
                    className="w-4 h-4 rounded border-gray-300 text-gappex-primary focus:ring-gappex-primary cursor-pointer"
                  />
                </td>

                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gappex-secondary whitespace-nowrap">
                      {fila.nombre}
                    </span>
                    <AnimatedBadge
                      show={calc.sinCostoParaGappex}
                      title={`El paciente paga ${formatCurrency2(
                        calc.pagoPaciente,
                      )}, por encima del precio acordado. El prestador cobra de más y Digital no pone nada.`}
                      className="bg-emerald-100 text-emerald-700 whitespace-nowrap"
                    >
                      sin costo
                    </AnimatedBadge>
                    {fila.categoria === null && (
                      <button
                        type="button"
                        onClick={() => onRemoveFila(index)}
                        title="Quitar esta práctica"
                        className="text-slate-300 hover:text-red-500 transition-colors"
                        aria-label={`Quitar ${fila.nombre}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>

                <td className="py-2 pr-3">
                  <Segmented<ModoFila>
                    options={MODO_OPCIONES}
                    value={fila.modo}
                    disabled={apagada}
                    ariaLabel={`Modo de cálculo de ${fila.nombre}`}
                    onChange={(modo) =>
                      onChangeFila(index, {
                        modo,
                        // Al invertir la fila se arranca del valor que estaba a la
                        // vista, para que el cambio de modo no mueva los números.
                        ...(modo === 'prestador'
                          ? { digitalPrestador: Math.round(calc.digitalEfectivo * 100) / 100 }
                          : { precioGappex: Math.round(calc.precioGappexEfectivo * 100) / 100 }),
                      })
                    }
                  />
                </td>

                <td className="py-2 pr-3 text-right">
                  <NumberInput
                    value={fila.descuentoPct}
                    onChange={(v) => onChangeFila(index, { descuentoPct: v })}
                    min={0}
                    max={100}
                    disabled={apagada}
                    suffix="%"
                    className="w-20"
                    ariaLabel={`Descuento de ${fila.nombre}`}
                  />
                </td>

                <td className="py-2 pr-3 text-right">
                  <NumberInput
                    value={fila.precioParticular}
                    onChange={(v) => onChangeFila(index, { precioParticular: v })}
                    min={0}
                    disabled={apagada}
                    prefix="$"
                    currency
                    className="w-28"
                    ariaLabel={`Precio particular de ${fila.nombre}`}
                  />
                </td>

                <td className="py-2 pr-3 text-right">
                  <NumberInput
                    value={fila.precioOsde}
                    onChange={(v) => onChangeFila(index, { precioOsde: v })}
                    min={0}
                    disabled={apagada}
                    prefix="$"
                    currency
                    className="w-28"
                    variant="plain"
                    ariaLabel={`Precio OSDE de referencia de ${fila.nombre}`}
                  />
                </td>

                <td className="py-2 pr-3 text-right">
                  {modoGappex ? (
                    <NumberInput
                      value={fila.precioGappex}
                      onChange={(v) => onChangeFila(index, { precioGappex: v })}
                      min={0}
                      disabled={apagada}
                      prefix="$"
                      currency
                      className="w-28"
                      ariaLabel={`Precio Gappex de ${fila.nombre}`}
                    />
                  ) : (
                    <CalcChip
                      accent
                      title="Precio que habría que acordar con el prestador: pago del paciente más lo que pone Digital."
                    >
                      {formatCurrency2(calc.precioGappexEfectivo)}
                    </CalcChip>
                  )}
                </td>

                <td className="py-2 pr-3 text-right">
                  <CalcChip
                    title={`Particular menos ${formatPct(fila.descuentoPct, 0)} de descuento. Descuento efectivo: ${formatPct(calc.descuentoEfectivoPct)}`}
                  >
                    {formatCurrency2(calc.pagoPaciente)}
                  </CalcChip>
                </td>

                <td className="py-2 pr-3 text-right">
                  {modoGappex ? (
                    <CalcChip
                      accent
                      title="Precio Gappex menos lo que paga el paciente. Nunca es negativo."
                    >
                      {formatCurrency2(calc.digitalEfectivo)}
                    </CalcChip>
                  ) : (
                    <NumberInput
                      value={fila.digitalPrestador}
                      onChange={(v) => onChangeFila(index, { digitalPrestador: v })}
                      min={0}
                      disabled={apagada}
                      prefix="$"
                      currency
                      className="w-28"
                      ariaLabel={`Digital a prestador de ${fila.nombre}`}
                    />
                  )}
                </td>

                <td className="py-2 pr-3 text-right">
                  <NumberInput
                    value={fila.cantMin}
                    onChange={(v) => onChangeFila(index, { cantMin: v })}
                    min={0}
                    disabled={apagada}
                    className="w-16"
                    ariaLabel={`Cantidad mínima anual de ${fila.nombre}`}
                  />
                </td>

                <td className="py-2 pr-3 text-right">
                  <NumberInput
                    value={fila.cantMax}
                    onChange={(v) => onChangeFila(index, { cantMax: v })}
                    min={0}
                    disabled={apagada}
                    className="w-16"
                    ariaLabel={`Cantidad máxima anual de ${fila.nombre}`}
                  />
                </td>

                <td className="py-2 text-right whitespace-nowrap text-xs text-gray-500 tabular-nums">
                  {formatCurrency2(calc.costoAnualMin)}
                  <span className="mx-1 text-gray-300">—</span>
                  {formatCurrency2(calc.costoAnualMax)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    <Button variant="ghost" onClick={onAddFila} className="mt-4">
      + Agregar práctica a {planNombre}
    </Button>
  </Card>
);

export default PrestacionesTable;
